import { Button, Container, Group, Progress, Stack, Text, Title } from "@mantine/core";
import { cancelJob, getJobStatus } from "../api";
import { useCallback, useEffect, useState } from "react";
import { usePolling } from "../hooks/usePolling";

// Short, human label per job type for the progress line.
const JOB_LABELS = {
  JobOptiviseBatch: "Optimising products",
  JobOptiviseProtoBatch: "Creating product variants",
  JobMapMiraklInputFile: "Mapping imported products",
  JobUpdateMiraklMarketplace: "Refreshing marketplace data",
  JobScrapeAsins: "Scraping product data",
  JobPreflightInspect: "Checking uploaded file",
};

const TERMINAL = ["success", "failed"];

export default function JobStatus({ params }) {
  const [status, setStatus] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  const job = status?.job;
  const jobStatus = job?.status; // camelCase enum over the wire: queued | processing | success | failed
  const isTerminal = TERMINAL.includes(jobStatus);
  const percent = Math.round((job?.progress ?? 0) * 100);
  const label = JOB_LABELS[status?.type] ?? "Working";

  const fetchUpdate = useCallback(() => {
    getJobStatus(params.jobId).then(setStatus);
  }, [params.jobId]);

  useEffect(() => {
    fetchUpdate();
  }, [fetchUpdate]);

  usePolling(fetchUpdate, {
    enabled: !isTerminal,
    intervalMs: 5000,
    invokeImmediately: false,
  });

  const onCancel = async () => {
    setCancelling(true);
    try {
      await cancelJob(params.jobId);
      fetchUpdate();
    } finally {
      setCancelling(false);
    }
  };

  const heading =
    {
      success: "Done!",
      failed: "Stopped",
      processing: "Lean back and relax!",
      queued: "Waiting to start…",
    }[jobStatus] ?? "Lean back and relax!";

  return (
    <Container size="xl">
      <Stack>
        <Title order={2}>{heading}</Title>

        <Progress
          value={percent}
          animated={!isTerminal}
          color={jobStatus === "failed" ? "red" : undefined}
        />

        <Group justify="space-between">
          <Text c="dimmed" size="sm">
            {isTerminal ? jobStatus : `${label} — ${percent}%`}
          </Text>

          {!isTerminal && (
            <Button
              variant="light"
              color="red"
              size="xs"
              loading={cancelling}
              disabled={job?.cancellationRequested}
              onClick={onCancel}
            >
              {job?.cancellationRequested ? "Cancelling…" : "Cancel"}
            </Button>
          )}
        </Group>
      </Stack>
    </Container>
  );
}
